import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { MONTHS } from '../utils/constants.js';
import { calcStats, calcMaxDrawdown, calcScore, calcConsecWinStreak, fmt$, scoreLabel } from '../utils/helpers.js';
import { useRadar, PerfOverviewChart } from '../hooks/useCharts.jsx';
import '../styles/dashboard-zella.css';

const ICON_TRADES  = <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>;
const ICON_WINRATE = <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>;
const ICON_NET     = <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>;
const ICON_RR      = <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/></svg>;
const ICON_STREAK  = <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>;
const ICON_PF      = <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="4" y1="20" x2="4" y2="12"/><line x1="12" y1="20" x2="12" y2="6"/><line x1="20" y1="20" x2="20" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></svg>;
const ICON_PAIR    = <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="8" r="6"/><path d="M8.5 13.5 7 22l5-3 5 3-1.5-8.5"/></svg>;

function ConfirmModal({ message, onConfirm, onCancel }) {
  return (
    <div className="confirm-modal-backdrop">
      <div className="confirm-modal-box" onClick={e => e.stopPropagation()}>
        <div className="h5 confirm-modal-title">Confirm</div>
        <div className="txt-body-sm txt-secondary confirm-modal-msg">{message}</div>
        <div className="confirm-modal-actions">
          <button onClick={onConfirm} className="txt-btn confirm-btn confirm-btn-danger">Confirm</button>
          <button onClick={onCancel}  className="txt-btn confirm-btn confirm-btn-cancel">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function PatternAlerts({ trades }) {
  const alerts = useMemo(() => {
    if (!trades.length) return [];
    const sorted = [...trades].sort((a, b) => {
      const d = a.date.localeCompare(b.date); if (d !== 0) return d;
      return (Number(a.id)||0) - (Number(b.id)||0);
    });
    const msgs = [];
    let consec = 0;
    for (let i = sorted.length - 1; i >= 0; i--) {
      if (sorted[i].result < 0) consec++; else break;
    }
    if (consec >= 3) msgs.push({ type:'critical', icon:'🛑', msg:`${consec} consecutive losses. Take a break — tilt risk is high.` });
    const avgLot = trades.reduce((s, t) => s + (parseFloat(t.lotSize)||0), 0) / trades.length;
    const recentLosses = sorted.slice(-5).filter(t => t.result < 0);
    if (recentLosses.length >= 2) {
      const recentAvgLot = recentLosses.reduce((s, t) => s + (parseFloat(t.lotSize)||0), 0) / recentLosses.length;
      if (recentAvgLot > avgLot * 1.3) msgs.push({ type:'warning', icon:'⚠️', msg:`Possible revenge sizing: recent losing lot avg is ${(recentAvgLot/avgLot*100-100).toFixed(0)}% above your normal.` });
    }
    return msgs.slice(0, 2);
  }, [trades]);

  if (!alerts.length) return null;
  return (
    <div className="pattern-alerts-wrap">
      {alerts.map((a, i) => (
        <div key={i} className={`pattern-alert ${a.type==='critical'?'pattern-alert-critical':'pattern-alert-warning'}`}>
          <span className="pattern-alert-icon">{a.icon}</span>
          <span className={`txt-caption fw-medium pattern-alert-msg ${a.type==='critical'?'pattern-alert-msg-critical':'pattern-alert-msg-warning'}`}>{a.msg}</span>
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   IMPORT MODAL  —  MT4 / MT5 / KFX CSV
───────────────────────────────────────────────────────────────── */
function ImportModal({ onImport, onClose, nextId, activeAccountId }) {
  const [mode,   setMode]   = useState('mt4');
  const [raw,    setRaw]    = useState('');
  const [parsed, setParsed] = useState(null);
  const [error,  setError]  = useState('');
  const fileRef = useRef(null);

  function parseMT4(text) {
    const lines = text.trim().split(/\r?\n/).filter(l => l.trim());
    const hIdx = lines.findIndex(l =>
      /ticket|order|open time|type|size|item|symbol|price|s\/l|t\/p|close time|commission|swap|profit/i.test(l)
    );
    if (hIdx === -1) throw new Error('Could not find MT4 header row. Make sure you exported a Detailed Statement.');
    const headers = lines[hIdx].split(',').map(h => h.trim().toLowerCase().replace(/[^a-z0-9 ]/g,'').trim());
    const col = k => headers.findIndex(h => h.includes(k));
    const cTicket = col('ticket'); const cOpenTime = col('open time');
    const cType = col('type'); const cSize = col('size');
    const cSymbol = col('item') !== -1 ? col('item') : col('symbol');
    const cPrice = col('open price') !== -1 ? col('open price') : col('price');
    const cSL = col('s/l'); const cTP = col('t/p');
    const cCloseTime = col('close time'); const cClosePrice = col('close price');
    const cProfit = col('profit');
    const trades = [];
    for (let i = hIdx + 1; i < lines.length; i++) {
      const row = lines[i].split(',').map(c => c.trim().replace(/"/g,''));
      if (!row[cType]) continue;
      const typeRaw = row[cType]?.toLowerCase() || '';
      if (!['buy','sell'].includes(typeRaw)) continue;
      const openDateRaw = row[cOpenTime] || '';
      const openDate = openDateRaw.split(' ')[0] || '';
      const openTime = openDateRaw.split(' ')[1]?.slice(0,5) || '';
      const closeTimeRaw = (row[cCloseTime] || '').split(' ')[1]?.slice(0,5) || '';
      const profit = parseFloat(row[cProfit]) || 0;
      const entryPrice = row[cPrice] || '';
      const exitPrice = row[cClosePrice] || '';
      const sl = row[cSL] || ''; const tp = row[cTP] || '';
      const lot = parseFloat(row[cSize]) || 0;
      const symbol = (row[cSymbol] || '').toUpperCase().replace(/[^A-Z0-9]/g,'');
      const isGold = symbol.includes('XAU') || symbol.includes('GOLD');
      const entryF = parseFloat(entryPrice), exitF = parseFloat(exitPrice);
      const pipsRaw = (typeRaw === 'buy' ? exitF - entryF : entryF - exitF) * (isGold ? 10 : 10000);
      const rr = sl && tp && entryPrice ? (() => {
        const risk = Math.abs(entryF - parseFloat(sl));
        const reward = Math.abs(parseFloat(tp) - entryF);
        return risk > 0 ? (reward / risk).toFixed(2) : '0.00';
      })() : '0.00';
      if (!openDate || !symbol) continue;
      trades.push({ pair: symbol, dir: typeRaw === 'buy' ? 'BUY' : 'SELL', date: openDate, time: openTime, exitTime: closeTimeRaw, entry: entryPrice, sl, tp, lotSize: lot, pips: parseFloat(pipsRaw.toFixed(isGold ? 2 : 1)), result: profit, rr, strat:'', session:'', emotion:'', notes:`Imported from MT4 · Ticket #${row[cTicket] || i}`, review:'', img:'' });
    }
    return trades;
  }

  function parseMT5(text) {
    const lines = text.trim().split(/\r?\n/).filter(l => l.trim());
    const hIdx = lines.findIndex(l => /time|deal|symbol|direction|volume|price|profit/i.test(l));
    if (hIdx === -1) throw new Error('Could not find MT5 header row.');
    const headers = lines[hIdx].split(',').map(h => h.trim().toLowerCase());
    const col = k => headers.findIndex(h => h.includes(k));
    const cTime = col('time'); const cSymbol = col('symbol');
    const cDir = col('direction'); const cVol = col('volume');
    const cPrice = col('price'); const cProfit = col('profit');
    const trades = [];
    for (let i = hIdx + 1; i < lines.length; i++) {
      const row = lines[i].split(',').map(c => c.trim().replace(/"/g,''));
      const dirRaw = row[cDir]?.toLowerCase() || '';
      if (!['in','out','in/out'].some(d => dirRaw.includes(d))) continue;
      const profit = parseFloat(row[cProfit]) || 0;
      const dateRaw = row[cTime] || '';
      const date = dateRaw.split(' ')[0] || '';
      const time = dateRaw.split(' ')[1]?.slice(0,5) || '';
      const symbol = (row[cSymbol] || '').toUpperCase().replace(/[^A-Z0-9]/g,'');
      const dir = dirRaw.includes('in') && !dirRaw.includes('out') ? 'BUY' : 'SELL';
      if (!date || !symbol) continue;
      trades.push({ pair: symbol, dir, date, time, exitTime:'', entry: row[cPrice] || '', sl:'', tp:'', lotSize: parseFloat(row[cVol]) || 0, pips: 0, result: profit, rr: '0.00', strat:'', session:'', emotion:'', notes:`Imported from MT5 · Row ${i}`, review:'', img:'' });
    }
    return trades;
  }

  function parseKFXCSV(text) {
    const lines = text.trim().split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) throw new Error('Empty CSV');
    const headers = lines[0].split(',').map(h => h.replace(/"/g,'').trim().toLowerCase());
    const col = k => { const exact = headers.indexOf(k); if (exact !== -1) return exact; return headers.findIndex(h => h.includes(k)); };
    const parseRow = line => { const result = []; let cur = '', inQ = false; for (let i = 0; i < line.length; i++) { const ch = line[i]; if (ch === '"') { inQ = !inQ; } else if (ch === ',' && !inQ) { result.push(cur.trim()); cur = ''; } else { cur += ch; } } result.push(cur.trim()); return result; };
    const trades = lines.slice(1).map(line => { const row = parseRow(line); return { pair: row[col('pair')] || '', dir: (row[col('dir')] || 'BUY').toUpperCase(), date: row[col('date')] || '', time: row[col('time')] || '', entry: row[col('entry')] || '', sl: row[col('sl')] || '', tp: row[col('tp')] || '', lotSize: parseFloat(row[col('lot')]) || 0, pips: parseFloat(row[col('pips')]) || 0, result: parseFloat(row[col('result')]) || 0, rr: row[col('r:r')] || row[col('rr')] || '0.00', strat: row[col('strategy')] || '', session: row[col('session')] || '', emotion: row[col('emotion')] || '', notes: row[col('notes')] || '', review: row[col('review')] || '', img: '' }; }).filter(t => t.pair && t.date);
    if (!trades.length) throw new Error(`No valid trades found. Headers detected: ${headers.join(', ')}`);
    return trades;
  }

  function handleParse() {
    setError(''); setParsed(null);
    try {
      let result;
      if (mode === 'mt4') result = parseMT4(raw);
      else if (mode === 'mt5') result = parseMT5(raw);
      else result = parseKFXCSV(raw);
      if (!result.length) throw new Error('No valid trades found. Check your format.');
      setParsed(result);
    } catch (e) { setError(e.message); }
  }

  function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setRaw(ev.target.result);
    reader.readAsText(file);
  }

  function handleConfirmImport() {
    if (!parsed?.length) return;
    if (typeof onImport !== 'function') { console.error('ImportModal: onImport is not a function'); return; }
    let idCounter = nextId;
    const withIds = parsed.map(t => ({ ...t, id: idCounter++, accountId: activeAccountId ?? null }));
    onImport(withIds, idCounter);
    onClose();
  }

  const HINTS = {
    mt4: <><b>MT4:</b> Account History → Right-click → <b>Save as Detailed Report</b> → copy CSV content or upload the file.</>,
    mt5: <><b>MT5:</b> History tab → Right-click → <b>Export to CSV</b>. Select <b>Deals</b> (not Positions).</>,
    csv: <>Paste a CSV previously exported from <b>KFX Journal</b>. Header must match KFX export format.</>,
  };

  return (
    <div className="modal-bg open" onClick={onClose}>
      <div className="modal import-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="modal-bar">
          <div>
            <div className="modal-ttl">Import Trades</div>
            <div className="modal-bar-sub">MetaTrader 4 · MetaTrader 5 · KFX CSV</div>
          </div>
          <button className="modal-x" onClick={onClose}>×</button>
        </div>

        <div className="import-modal-body">

          {/* Source selector */}
          <div className="import-source-tabs">
            {[['mt4','MT4','History CSV'],['mt5','MT5','Deals CSV'],['csv','KFX','CSV Export']].map(([v,main,sub]) => (
              <button key={v} className={`import-source-tab${mode===v?' active':''}`}
                onClick={() => { setMode(v); setParsed(null); setError(''); }}>
                <div className="tab-main">{main}</div>
                <div className="tab-sub">{sub}</div>
              </button>
            ))}
          </div>

          {/* Hint */}
          <div className="import-hint-box">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5B8FE8" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
            <div className="import-hint-text">{HINTS[mode]}</div>
          </div>

          {/* Drop zone */}
          <div className="import-dropzone">
            <div className="import-upload-row">
              <button className="import-file-btn" onClick={() => fileRef.current?.click()}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                Upload File
              </button>
              <span className="import-or-label">or paste below</span>
              <input ref={fileRef} type="file" accept=".csv,.txt,.htm,.html" className="import-hidden-input" onChange={handleFileUpload} />
            </div>
            <textarea
              className="import-paste-area"
              value={raw}
              onChange={e => { setRaw(e.target.value); setParsed(null); setError(''); }}
              placeholder={`Paste your ${mode.toUpperCase()} CSV content here…`}
            />
          </div>

          {/* Error */}
          {error && (
            <div className="import-error-box">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="import-error-icon">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {error}
            </div>
          )}

          {/* Success preview */}
          {parsed && (
            <div className="import-success-box">
              <div className="import-success-hd">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                <span className="import-success-title">Parsed successfully</span>
                <span className="import-success-count">{parsed.length} trades detected</span>
              </div>
              <div className="import-preview-scroll">
                <table className="import-preview-table">
                  <thead>
                    <tr>{['Date','Pair','Dir','Entry','Lot','P&L','Session'].map(h=><th key={h}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {parsed.slice(0,8).map((t,i) => (
                      <tr key={i}>
                        <td>{t.date}</td>
                        <td className="import-cell-pair">{t.pair}</td>
                        <td className={t.dir==='BUY'?'import-cell-dir-buy':'import-cell-dir-sell'}>{t.dir}</td>
                        <td>{t.entry}</td>
                        <td>{t.lotSize}</td>
                        <td className={t.result>=0?'import-cell-result-pos':'import-cell-result-neg'}>{t.result>=0?'+':'-'}${Math.abs(t.result).toFixed(2)}</td>
                        <td>{t.session||'—'}</td>
                      </tr>
                    ))}
                    {parsed.length > 8 && <tr><td colSpan={7} className="import-more-row">+ {parsed.length-8} more trades</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="import-actions">
            {!parsed
              ? <button className="btn-cta" onClick={handleParse} disabled={!raw.trim()}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                  Parse CSV
                </button>
              : <button className="btn-cta" onClick={handleConfirmImport}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                  Import {parsed.length} Trades
                </button>
            }
            <button className="btn-outline" onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const MIN_TRADES_FOR_SCORE = 10;

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT — Clean Dashboard: Stats + Calendar + Recent
═══════════════════════════════════════════════════════════════ */
/* ── Account switcher pill ──────────────────────────────────────────────────── */
const TYPE_COLOR = { Live: 'var(--green)', Demo: 'rgba(91,143,232,.9)', 'Prop Firm': 'var(--gold)', Paper: 'rgba(200,192,168,.6)' };

function AccountSwitcher({ accounts, activeId, onChange }) {
  if (!accounts.length) return (
    <button
      onClick={() => window.dispatchEvent(new CustomEvent('kfx:settings-navigate', { detail: { section: 'accounts' } }))}
      className="account-switcher-add-btn"
    >
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
      Add Account
    </button>
  );
  const active = accounts.find(a => a.id === activeId);
  return (
    <div className="account-switcher-wrap">
      <select
        value={activeId || ''}
        onChange={e => onChange(e.target.value)}
        className="account-switcher-select"
        style={{ '--acct-color': active ? (TYPE_COLOR[active.type] || 'var(--gold)') : undefined }}
      >
        {accounts.map(a => (
          <option key={a.id} value={a.id}>
            {a.name}{a.balance ? ` · $${parseFloat(a.balance).toLocaleString()}` : ''}
          </option>
        ))}
      </select>
      <svg className="account-switcher-caret" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
    </div>
  );
}

export default function DashboardPage({ trades: allTrades, setPage, onNewTrade, onImportTrades, nextId = 1, activeAccount: propActiveAccount, tradingAccounts: propTradingAccounts = [], userInfo }) {

  /* ── Account state — synced from props and kfx:accounts-updated event ── */
  const [tradingAccounts, setTradingAccounts] = useState(propTradingAccounts);
  const [activeAccountId, setActiveAccountId] = useState(propActiveAccount?.id || null);

  useEffect(() => { setTradingAccounts(propTradingAccounts); }, [propTradingAccounts]);
  useEffect(() => { setActiveAccountId(propActiveAccount?.id || null); }, [propActiveAccount]);

  useEffect(() => {
    const handler = ({ detail }) => {
      if (Array.isArray(detail.tradingAccounts)) setTradingAccounts(detail.tradingAccounts);
      if (detail.activeAccountId !== undefined)  setActiveAccountId(detail.activeAccountId);
    };
    window.addEventListener('kfx:accounts-updated', handler);
    return () => window.removeEventListener('kfx:accounts-updated', handler);
  }, []);

  /* ── Derive activeAccount object ── */
  const activeAccount = tradingAccounts.find(a => a.id === activeAccountId) ?? null;

  /* ── Filter trades to active account (legacy trades with no accountId always show) ── */
  const trades = useMemo(() => {
    if (!activeAccountId) return allTrades;
    return allTrades.filter(t => t.accountId === activeAccountId || !t.accountId);
  }, [allTrades, activeAccountId]);

  const s               = useMemo(() => calcStats(trades), [trades]);
  const cleanWeeks      = useMemo(() => calcConsecWinStreak(trades), [trades]);
  const radarRef        = useRef(null);
  useRadar(radarRef, { ...s, weekStreak: cleanWeeks });

  const [rpTab, setRpTab] = useState('score');
  const [calDayModal, setCalDayModal] = useState(null);
  const [viewTrade,   setViewTrade]   = useState(null);
  const [perfFilter,  setPerfFilter]  = useState('ALL');
  const [recentLimit, setRecentLimit] = useState(3);
  const [confirm,     setConfirm]     = useState(null);
  const [showImport,  setShowImport]  = useState(false);

  // ── Calendar extras ───────────────────────────────────────────
  const calRef            = useRef(null);
  const calSettingsRef    = useRef(null);
  const [calSettingsOpen, setCalSettingsOpen] = useState(false);
  const [hideTradeCount,  setHideTradeCount]  = useState(false);
  const [hideWinPct,      setHideWinPct]      = useState(false);
  const [screenshotting,  setScreenshotting]  = useState(false);

  // Close settings dropdown on outside click
  useEffect(() => {
    if (!calSettingsOpen) return;
    const handler = (e) => {
      if (calSettingsRef.current && !calSettingsRef.current.contains(e.target)) {
        setCalSettingsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [calSettingsOpen]);

  const today = new Date();
  const [calYear,  setCalYear]  = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());

  /* ── Realized R multiple for a single trade ──
     Uses the trade's ACTUAL dollar risk (riskAmount — computed at entry from
     entry/SL/lotSize/pair, so it already reflects whatever % the user really
     risked: 0.5%, 1%, 2%, whatever varies trade to trade) rather than assuming
     every loss was a flat -1R. Falls back to the old convention only for older/
     imported trades that don't have a riskAmount stored. */
  const tradeR = useCallback((t) => {
    const riskAmt = parseFloat(t.riskAmount);
    if (riskAmt > 0) return t.result / riskAmt;
    // Fallback (no stored risk $ — e.g. MT4/MT5 import): winners use their R:R, losers assume -1R
    return t.result > 0 ? (parseFloat(t.actualRR || t.rr) || 0) : t.result < 0 ? -1 : 0;
  }, []);

  /* ── Calendar data ── */
  const calData = useMemo(() => {
    const dayMap = {};
    trades.forEach(t => {
      const d = new Date(t.date);
      if (d.getFullYear() === calYear && d.getMonth() === calMonth) {
        const day = d.getDate();
        if (!dayMap[day]) dayMap[day] = { pnl:0, rr:0, count:0, wins:0, trades:[], sessions:new Set() };
        dayMap[day].pnl += t.result; dayMap[day].count++;
        if (t.result > 0) dayMap[day].wins++;
        dayMap[day].rr += tradeR(t);
        dayMap[day].trades.push(t);
        if (t.session) dayMap[day].sessions.add(t.session);
      }
    });
    // Convert sessions Set → sorted Array for easy render
    Object.values(dayMap).forEach(d => { d.sessions = [...d.sessions].sort(); });

    const monthPnl  = Object.values(dayMap).reduce((a, d) => a + d.pnl, 0);
    const monthRR   = Object.values(dayMap).reduce((a, d) => a + d.rr, 0);
    const monthDays = Object.keys(dayMap).length;
    const monthWins = Object.values(dayMap).reduce((a, d) => a + d.wins, 0);
    const monthTrades = Object.values(dayMap).reduce((a, d) => a + d.count, 0);
    const monthWR   = monthTrades > 0 ? Math.round(monthWins / monthTrades * 100) : 0;

    const dayEntries = Object.entries(dayMap).map(([k, v]) => ({ day: +k, ...v }));
    const bestDay    = dayEntries.length ? dayEntries.reduce((a, b) => b.pnl > a.pnl ? b : a) : null;
    const worstDay   = dayEntries.length ? dayEntries.reduce((a, b) => b.pnl < a.pnl ? b : a) : null;
    const bestDayRR  = dayEntries.length ? dayEntries.reduce((a, b) => b.rr > a.rr ? b : a) : null;
    const worstDayRR = dayEntries.length ? dayEntries.reduce((a, b) => b.rr < a.rr ? b : a) : null;

    const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
    const firstDay    = new Date(calYear, calMonth, 1).getDay();
    const weeks = [];
    for (let w = 0; w < Math.ceil((firstDay + daysInMonth) / 7); w++) {
      const days = [];
      for (let d = 0; d < 7; d++) { const cd = w*7+d-firstDay+1; days.push(cd>=1&&cd<=daysInMonth?cd:null); }
      const traded = days.filter(d => d && dayMap[d]);
      weeks.push({ n:w+1, days, pnl:traded.reduce((a,d)=>a+dayMap[d].pnl,0), rr:traded.reduce((a,d)=>a+dayMap[d].rr,0), tradeDays:traded.length });
    }
    return { dayMap, monthPnl, monthRR, monthDays, monthWR, bestDay, worstDay, bestDayRR, worstDayRR, daysInMonth, firstDay, weeks };
  }, [trades, calYear, calMonth, tradeR]);

  const { dayMap, monthPnl, monthRR, monthDays, monthWR, bestDay, worstDay, bestDayRR, worstDayRR, daysInMonth, firstDay, weeks } = calData;

  /* ── Calendar display mode: show $ P&L or R:R per day ── */
  const [calValueMode, setCalValueMode] = useState('money'); // 'money' | 'rr'
  const fmtCalVal = (val) => calValueMode === 'rr' ? `${val>=0?'+':''}${val.toFixed(2)}R` : fmt$(val);

  /* ── All traded days (for day-modal prev/next paging, across any month) ── */
  const allDaysData = useMemo(() => {
    const map = {};
    trades.forEach(t => {
      const d = new Date(t.date);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map[key]) map[key] = { year: d.getFullYear(), month: d.getMonth(), day: d.getDate(), pnl: 0, count: 0, trades: [] };
      map[key].pnl += t.result; map[key].count++; map[key].trades.push(t);
    });
    return Object.values(map).sort((a, b) => new Date(a.year, a.month, a.day) - new Date(b.year, b.month, b.day));
  }, [trades]);

  /* ── Recent form ── */
  const recentFormData = useMemo(() => {
    const sorted = [...trades].sort((a,b) => {
      const d = a.date.localeCompare(b.date); if (d !== 0) return d;
      return (Number(a.id)||0) - (Number(b.id)||0);
    });
    const last10 = sorted.slice(-10);
    const recentWR  = last10.length ? Math.round(last10.filter(t=>t.result>0).length/last10.length*100) : 0;
    const recentPnl = Math.round(last10.reduce((s,t)=>s+t.result,0));
    const trend     = trades.length >= 10 ? (recentWR > s.wr ? 'IMPROVING' : recentWR < s.wr ? 'DECLINING' : 'STABLE') : null;
    return { recentWR, recentPnl, trend };
  }, [trades, s.wr]);

  const { recentWR, recentPnl, trend } = recentFormData;
  const winPct = s.total ? s.wins / s.total * 100 : 0;
  const recent = [...trades].sort((a,b) => {
    const d = b.date.localeCompare(a.date); if (d !== 0) return d;
    return (Number(b.id)||0) - (Number(a.id)||0);
  }).slice(0, recentLimit);

  /* ── Max Drawdown (for score) ── */
  const { maxDD, maxDDPct, sessionList } = useMemo(() => {
    const { maxDD, maxDDPct } = calcMaxDrawdown(trades);
    const sessionMap = {};
    trades.forEach(t => {
      const k = t.session || 'Unknown';
      if (!sessionMap[k]) sessionMap[k] = { pnl:0, n:0, wins:0 };
      sessionMap[k].pnl += t.result; sessionMap[k].n++;
      if (t.result > 0) sessionMap[k].wins++;
    });
    return { maxDD, maxDDPct, sessionList: Object.entries(sessionMap).sort((a,b) => b[1].pnl - a[1].pnl) };
  }, [trades]);

  const score = useMemo(() => calcScore(s, maxDDPct, trades.length), [s, maxDDPct, trades.length]);

  /* ── Gamification scores ── */
  const disciplineScore = useMemo(() => {
    if (!trades.length) return null;
    const rrOk   = trades.filter(t => parseFloat(t.rr || 0) >= 1).length / trades.length;
    const slOk   = trades.filter(t => t.sl && parseFloat(t.sl) > 0).length / trades.length;
    const sesOk  = trades.filter(t => t.session).length / trades.length;
    return Math.round((rrOk * 0.4 + slOk * 0.35 + sesOk * 0.25) * 100);
  }, [trades]);

  const consistencyScore = useMemo(() => {
    if (trades.length < 5) return null;
    const pfNum  = parseFloat(s.pf) || 0;
    const pfScr  = Math.min(pfNum / 3, 1);
    const ddScr  = Math.max(0, 1 - maxDDPct / 20);
    const wrScr  = Math.min((s.wr - 40) / 40, 1);
    return Math.round((pfScr * 0.35 + ddScr * 0.35 + wrScr * 0.30) * 100);
  }, [s, maxDDPct, trades]);

  const propFirmReadiness = useMemo(() => {
    if (!trades.length) return null;
    const pfNum  = parseFloat(s.pf) || 0;
    const rrNum  = parseFloat(s.avgrr) || 0;
    const wrScr  = Math.min((s.wr - 30) / 40, 1);
    const pfScr  = Math.min((pfNum - 1) / 2, 1);
    const ddScr  = Math.max(0, 1 - maxDDPct / 10);
    const rrScr  = Math.min(rrNum / 3, 1);
    return Math.max(0, Math.round((wrScr * 0.25 + pfScr * 0.25 + ddScr * 0.30 + rrScr * 0.20) * 100));
  }, [s, maxDDPct, trades]);

  /* ── AI Coach Insights (rule-based) ── */
  const aiInsights = useMemo(() => {
    if (trades.length < 5) return null;
    const rrNum = parseFloat(s.avgrr) || 0;
    const pfNum = parseFloat(s.pf) || 0;

    // Best session
    const bestSession = sessionList[0];
    const worstSession = sessionList[sessionList.length - 1];

    // Pair data
    const pairMap = {};
    trades.forEach(t => {
      const k = t.pair || 'Unknown';
      if (!pairMap[k]) pairMap[k] = { pnl: 0, n: 0, wins: 0 };
      pairMap[k].pnl += t.result; pairMap[k].n++;
      if (t.result > 0) pairMap[k].wins++;
    });
    const pairList = Object.entries(pairMap).sort((a, b) => b[1].pnl - a[1].pnl);
    const worstPair = pairList[pairList.length - 1];

    // Avg hold-time proxy: trades with long loss vs win
    const wins  = trades.filter(t => t.result > 0);
    const losses = trades.filter(t => t.result < 0);
    const avgWin  = wins.length  ? wins.reduce((a, t) => a + t.result, 0) / wins.length : 0;
    const avgLoss = losses.length ? losses.reduce((a, t) => a + Math.abs(t.result), 0) / losses.length : 0;
    const holdRatio = avgWin > 0 && avgLoss > 0 ? (avgLoss / avgWin) : 1;

    const strength = bestSession
      ? `${bestSession[0]} session execution (${Math.round(bestSession[1].wins / bestSession[1].n * 100)}% WR). This is your clearest edge.`
      : `Profit factor of ${pfNum.toFixed(2)} — your winners significantly outweigh your losers.`;

    const weakness = holdRatio > 1.2
      ? `Holding losers too long — avg loss is ${holdRatio.toFixed(1)}× your avg win size. Tighten your loss exits.`
      : rrNum < 1.5
        ? `Low average R:R of ${rrNum.toFixed(2)}R. Widen take-profits or tighten stop-losses before entry.`
        : s.wr < 50
          ? `Win rate below 50% (${s.wr}%). Focus on A+ setups only; reduce trade frequency.`
          : `${worstSession ? worstSession[0] + ' session' : 'Tokyo session'} underperformance. Consider avoiding those hours.`;

    const action = worstPair && worstPair[1].pnl < 0
      ? `Reduce ${worstPair[0]} exposure by 50% until you hit 3 consecutive profitable weeks on it. It's your biggest P&L leak.`
      : holdRatio > 1.2
        ? `Set a hard time-stop: if a trade isn't moving in your favour within 45 min, exit at market. Start with your worst-performing pair.`
        : `Focus exclusively on ${bestSession ? bestSession[0] : 'London'} session entries for the next 2 weeks to compound your strongest edge.`;

    const uplift = Math.round(Math.abs(s.net) * 0.12);

    return { strength, weakness, action, uplift };
  }, [trades, s, sessionList]);

  /* ── Monthly growth vs previous month ── */
  const monthlyGrowth = useMemo(() => {
    const now     = new Date();
    const curM    = now.getMonth(), curY = now.getFullYear();
    const prevM   = curM === 0 ? 11 : curM - 1;
    const prevY   = curM === 0 ? curY - 1 : curY;
    const curPnl  = trades.filter(t => { const d = new Date(t.date); return d.getMonth() === curM && d.getFullYear() === curY; }).reduce((a, t) => a + t.result, 0);
    const prevPnl = trades.filter(t => { const d = new Date(t.date); return d.getMonth() === prevM && d.getFullYear() === prevY; }).reduce((a, t) => a + t.result, 0);
    // Only show % when previous month had meaningful volume (avoid -96% from tiny base)
    const hasMeaningfulBase = Math.abs(prevPnl) > 50;
    const rawPct  = hasMeaningfulBase ? ((curPnl - prevPnl) / Math.abs(prevPnl) * 100) : 0;
    // Cap display at ±999% to prevent layout breaking
    const pct     = Math.max(-999, Math.min(999, parseFloat(rawPct.toFixed(1))));
    return { curPnl, prevPnl, pct, hasMeaningfulBase };
  }, [trades]);

  // ── Calendar screenshot ───────────────────────────────────────
  const handleCalScreenshot = () => {
    if (screenshotting) return;
    setScreenshotting(true);
    try {
      // ── Colours (hardcoded — avoids CSS variable resolution issues) ──
      const BG        = '#0b0c0f';
      const BG2       = '#13151a';
      const BG_POS    = 'rgba(46,160,90,0.18)';
      const BG_NEG    = 'rgba(200,60,60,0.18)';
      const GREEN     = '#3dc87a';
      const RED       = '#d94f4f';
      const GOLD      = '#C9A84C';
      const TEXT      = '#F0EAD6';
      const TEXT_DIM  = 'rgba(240,234,214,0.35)';
      const BORDER    = 'rgba(255,255,255,0.07)';

      const DAYS   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
      const SCALE  = 2;
      const PAD    = 28;
      const COL_W  = 120;
      const ROW_H  = 96;
      const WEEK_W = 110;
      const HDR_H  = 52;   // month title bar
      const DOW_H  = 30;   // day-of-week labels

      const numRows  = Math.ceil((firstDay + daysInMonth) / 7);
      const gridW    = COL_W * 7;
      const gridH    = DOW_H + numRows * ROW_H;
      const totalW   = PAD + gridW + 16 + WEEK_W + PAD;
      const totalH   = PAD + HDR_H + gridH + PAD;

      const canvas   = document.createElement('canvas');
      canvas.width   = totalW * SCALE;
      canvas.height  = totalH * SCALE;
      const ctx      = canvas.getContext('2d');
      ctx.scale(SCALE, SCALE);

      // ── Background ──────────────────────────────────────────────
      ctx.fillStyle = BG;
      ctx.fillRect(0, 0, totalW, totalH);

      // ── Header bar ─────────────────────────────────────────────
      ctx.fillStyle = BG2;
      ctx.fillRect(0, 0, totalW, PAD + HDR_H);
      // Gold left accent
      ctx.fillStyle = GOLD;
      ctx.fillRect(0, 0, 3, PAD + HDR_H);

      // LEFT — brand label + month/year
      ctx.font = `700 10px Inter,sans-serif`;
      ctx.fillStyle = TEXT_DIM;
      ctx.textAlign = 'left';
      ctx.fillText('KFX JOURNAL · TRADE CALENDAR', PAD, 20);

      ctx.font = `700 24px Inter,sans-serif`;
      ctx.fillStyle = TEXT;
      ctx.fillText(`${MONTHS[calMonth].toUpperCase()} ${calYear}`, PAD, 50);

      // RIGHT — P&L and days traded, right-aligned
      const pnlAmt  = fmtCalVal(calValueMode==='rr' ? monthRR : monthPnl);
      const daysLbl = `${monthDays} DAYS TRADED`;
      const rightX  = totalW - PAD;

      ctx.font = `600 11px Inter,sans-serif`;
      ctx.fillStyle = GOLD;
      ctx.textAlign = 'right';
      ctx.fillText(daysLbl, rightX, 20);

      ctx.font = `700 20px Inter,sans-serif`;
      ctx.fillStyle = (calValueMode==='rr' ? monthRR : monthPnl) >= 0 ? GREEN : RED;
      ctx.fillText(pnlAmt, rightX, 50);

      ctx.textAlign = 'left';

      const gridTop = PAD + HDR_H;

      // ── Day-of-week row ─────────────────────────────────────────
      DAYS.forEach((d, i) => {
        ctx.font = `600 10px Inter,sans-serif`;
        ctx.fillStyle = TEXT_DIM;
        ctx.textAlign = 'center';
        ctx.fillText(d.toUpperCase(), PAD + i * COL_W + COL_W / 2, gridTop + 20);
      });
      ctx.textAlign = 'left';

      // ── Grid cells ──────────────────────────────────────────────
      const today2 = new Date();
      for (let row = 0; row < numRows; row++) {
        for (let col = 0; col < 7; col++) {
          const dayNum = row * 7 + col - firstDay + 1;
          if (dayNum < 1 || dayNum > daysInMonth) continue;

          const x  = PAD + col * COL_W;
          const y  = gridTop + DOW_H + row * ROW_H;
          const d  = dayMap[dayNum];
          const isToday = today2.getFullYear() === calYear && today2.getMonth() === calMonth && today2.getDate() === dayNum;

          // Cell background
          const dispVal = d ? (calValueMode==='rr' ? d.rr : d.pnl) : 0;
          ctx.fillStyle = d ? (dispVal > 0 ? BG_POS : BG_NEG) : BG2;
          roundRect(ctx, x + 2, y + 2, COL_W - 4, ROW_H - 4, 6);
          ctx.fill();

          // Today border
          if (isToday) {
            ctx.strokeStyle = GOLD;
            ctx.lineWidth = 1.5;
            roundRect(ctx, x + 2, y + 2, COL_W - 4, ROW_H - 4, 6);
            ctx.stroke();
          }

          // Grid line
          ctx.strokeStyle = BORDER;
          ctx.lineWidth = 0.5;
          ctx.strokeRect(x, y, COL_W, ROW_H);

          // Day number
          ctx.font = `500 11px Inter,sans-serif`;
          ctx.fillStyle = isToday ? GOLD : (d ? TEXT : TEXT_DIM);
          ctx.fillText(String(dayNum), x + 8, y + 16);

          if (d) {
            // P&L or R:R
            ctx.font = `700 14px Inter,sans-serif`;
            ctx.fillStyle = dispVal > 0 ? GREEN : RED;
            ctx.fillText(fmtCalVal(dispVal), x + 8, y + 38);
            // Trade count
            if (!hideTradeCount) {
              ctx.font = `400 10px Inter,sans-serif`;
              ctx.fillStyle = TEXT_DIM;
              ctx.fillText(`${d.count} trade${d.count > 1 ? 's' : ''}`, x + 8, y + 54);
            }
            // Win %
            if (!hideWinPct) {
              const wr = Math.round(d.wins / d.count * 100);
              ctx.font = `600 11px Inter,sans-serif`;
              ctx.fillStyle = dispVal > 0 ? GREEN : RED;
              ctx.fillText(`${wr}%`, x + 8, y + 70);
            }
          }
        }
      }

      // ── Week column ─────────────────────────────────────────────
      const wkX = PAD + gridW + 16;
      weeks.forEach((wk, i) => {
        const y = gridTop + DOW_H + i * ROW_H;
        ctx.fillStyle = BG2;
        roundRect(ctx, wkX, y + 2, WEEK_W - 4, ROW_H - 4, 6);
        ctx.fill();
        ctx.strokeStyle = BORDER;
        ctx.lineWidth = 0.5;
        ctx.strokeRect(wkX, y, WEEK_W, ROW_H);

        ctx.font = `600 9px Inter,sans-serif`;
        ctx.fillStyle = TEXT_DIM;
        ctx.textAlign = 'right';
        ctx.fillText(`WEEK ${wk.n}`, wkX + WEEK_W - 10, y + 16);

        if (wk.tradeDays > 0) {
          const wkVal = calValueMode==='rr' ? wk.rr : wk.pnl;
          ctx.font = `700 16px Inter,sans-serif`;
          ctx.fillStyle = wkVal >= 0 ? GREEN : RED;
          ctx.textAlign = 'right';
          ctx.fillText(fmtCalVal(wkVal), wkX + WEEK_W - 10, y + 38);
          ctx.font = `400 10px Inter,sans-serif`;
          ctx.fillStyle = TEXT_DIM;
          ctx.fillText(`${wk.tradeDays} day${wk.tradeDays > 1 ? 's' : ''}`, wkX + WEEK_W - 10, y + 54);
        } else {
          ctx.font = `700 18px Inter,sans-serif`;
          ctx.fillStyle = 'rgba(240,234,214,0.08)';
          ctx.textAlign = 'right';
          ctx.fillText('OP', wkX + WEEK_W - 10, y + 40);
          ctx.font = `400 10px Inter,sans-serif`;
          ctx.fillStyle = TEXT_DIM;
          ctx.fillText('No trades', wkX + WEEK_W - 10, y + 56);
        }
        ctx.textAlign = 'left';
      });

      // ── Footer ──────────────────────────────────────────────────
      ctx.font = `400 9px Inter,sans-serif`;
      ctx.fillStyle = 'rgba(240,234,214,0.2)';
      ctx.fillText(`KFX Portal · Generated ${new Date().toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}`, PAD, totalH - 10);

      // ── Download ────────────────────────────────────────────────
      const link = document.createElement('a');
      link.download = `KFX-Calendar-${MONTHS[calMonth]}-${calYear}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('[calScreenshot]', err);
      alert('Screenshot failed — please try again.');
    } finally {
      setScreenshotting(false);
    }
  };

  // ── Canvas helper: rounded rect ──────────────────────────────────
  function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }
  const sl    = score !== null ? scoreLabel(score) : null;

  /* ── Empty state ── */
  if (!trades.length) return (
    <div className="empty-state">
      <div className="empty-state-icon">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="1.5">
          <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/>
          <polyline points="16 7 22 7 22 13"/>
        </svg>
      </div>
      <div className="empty-state-title">Welcome to <span>KFX Journaling</span></div>
      <div className="empty-state-body">
        Your trading journal is empty. Log your first trade manually or import directly from MetaTrader to start tracking your performance.
      </div>
      <div className="empty-state-actions">
        <button className="btn-cta" onClick={() => onNewTrade({ activeAccount })}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="u-mr-6"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Log Your First Trade
        </button>
        <button className="btn-outline" onClick={() => setShowImport(true)}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="u-mr-6"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
          Import MT4 / MT5
        </button>
      </div>
      <div className="empty-state-steps">
        {[['1','Log a trade'],['2','Review stats'],['3','Get AI coaching']].map(([n,l]) => (
          <div key={n} className="empty-step">
            <div className="empty-step-num">{n}</div>
            <div className="empty-step-label">{l}</div>
          </div>
        ))}
      </div>
      {showImport && <ImportModal onImport={onImportTrades} onClose={() => setShowImport(false)} nextId={nextId} activeAccountId={activeAccountId}/>}
    </div>
  );

  /* ── Donut helper ── */
  const wins   = trades.filter(t => t.result > 0).length;
  const losses = trades.filter(t => t.result < 0).length;
  const winPctFull = s.total ? (s.wins / s.total) * 100 : 0;
  const CIRC = 2 * Math.PI * 20;

  return (
    <div className="pg-d pg-d--zella">

      <PatternAlerts trades={trades}/>

      {/* ═══════════════════════════════════════════════════════════
          TOP BAR
      ═══════════════════════════════════════════════════════════ */}
      <div className="zl-topbar">
        <div>
          <div className="zl-topbar-title">Dashboard</div>
        </div>
        <div className="zl-topbar-actions">
          <button className="zl-btn-icon" title="Import trades" onClick={() => setShowImport(true)}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            <span>Import</span>
          </button>
          <button className="zl-btn-icon" title="Export PDF" onClick={() => exportDashboardPDF(s, { maxDD, maxDDPct, sessionList }, trades, radarRef, userInfo, activeAccount)}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          KPI STRIP — 5 cards
      ═══════════════════════════════════════════════════════════ */}
      <div className="zl-kpi-strip">

        {/* 1. Net P&L */}
        <div className={`zl-kpi zl-kpi--${s.net >= 0 ? 'green' : 'red'}`}>
          <div className="zl-kpi-label">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
            Net P&amp;L
            <span style={{fontSize:'9px',color:'var(--gray)',marginLeft:'auto'}}>{s.total} trades</span>
          </div>
          <div className="zl-kpi-body">
            <div className={`zl-kpi-num zl-kpi-num--${s.net >= 0 ? 'green' : 'red'}`}>{fmt$(s.net)}</div>
            {/* sparkline */}
            {(() => {
              const sorted = [...trades].sort((a,b) => a.date.localeCompare(b.date));
              let run = 0;
              const pts = sorted.map(t => { run += t.result; return run; });
              if (pts.length < 2) return null;
              const mn = Math.min(...pts), mx = Math.max(...pts), range = mx - mn || 1;
              const W = 80, H = 36;
              const color = s.net >= 0 ? 'var(--green)' : 'var(--red)';
              const pathD = pts.map((v,i) => `${i===0?'M':'L'}${(i/(pts.length-1))*W},${H-((v-mn)/range)*(H-4)+2}`).join(' ');
              return (
                <svg viewBox={`0 0 ${W} ${H}`} style={{width:80,height:36,flexShrink:0}} preserveAspectRatio="none">
                  <defs><linearGradient id="zlSpk" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity=".2"/><stop offset="100%" stopColor={color} stopOpacity="0"/></linearGradient></defs>
                  <path d={`${pathD} L${W},${H} L0,${H} Z`} fill="url(#zlSpk)"/>
                  <path d={pathD} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              );
            })()}
          </div>
        </div>

        {/* 2. Trade Win % */}
        <div className={`zl-kpi zl-kpi--${s.wr >= 50 ? 'green' : 'red'}`}>
          <div className="zl-kpi-label">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>
            Trade win %
          </div>
          <div className="zl-kpi-body">
            <div className={`zl-kpi-num zl-kpi-num--${s.wr >= 50 ? 'green' : 'red'}`}>{s.wr}%</div>
            <div className="zl-kpi-donut">
              <svg viewBox="0 0 44 44">
                <circle cx="22" cy="22" r="17" fill="none" stroke="rgba(255,255,255,.07)" strokeWidth="4.5"/>
                <circle cx="22" cy="22" r="17" fill="none"
                  stroke={s.wr >= 50 ? 'var(--green)' : 'var(--red)'}
                  strokeWidth="4.5"
                  strokeDasharray={`${2*Math.PI*17}`}
                  strokeDashoffset={`${2*Math.PI*17*(1-winPctFull/100)}`}
                  strokeLinecap="round"
                  transform="rotate(-90 22 22)"/>
                {/* losses arc (red fill behind green) */}
              </svg>
              <div className="zl-kpi-donut-label">
                <span className="zl-kpi-donut-main">{s.wins}</span>
                <span className="zl-kpi-donut-sub">{s.losses}</span>
              </div>
            </div>
          </div>
          <div style={{fontSize:'9px',color:'var(--gray)',marginTop:2}}>
            <span style={{color:'var(--green)'}}>{s.wins}W</span>
            {' · '}
            <span style={{color:'var(--red)'}}>{s.losses}L</span>
            {s.total - s.wins - s.losses > 0 && <span> · {s.total - s.wins - s.losses} BE</span>}
          </div>
        </div>

        {/* 3. Avg Win/Loss */}
        <div className="zl-kpi zl-kpi--blue">
          <div className="zl-kpi-label">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
            Avg win/loss trade
          </div>
          <div className="zl-kpi-body">
            <div className="zl-kpi-num zl-kpi-num--white">{s.avgrr}R</div>
            <div className="zl-kpi-bar-wrap">
              <div className="zl-kpi-bar-row">
                <div className="zl-kpi-bar-track">
                  <div className="zl-kpi-bar-fill--green" style={{width:`${Math.min(100,(s.aw/(s.aw+Math.abs(s.al)||1))*100)}%`}}/>
                </div>
                <span className="zl-kpi-bar-val" style={{color:'var(--green)'}}>{fmt$(s.aw)}</span>
              </div>
              <div className="zl-kpi-bar-row">
                <div className="zl-kpi-bar-track">
                  <div className="zl-kpi-bar-fill--red" style={{width:`${Math.min(100,(Math.abs(s.al)/(s.aw+Math.abs(s.al)||1))*100)}%`}}/>
                </div>
                <span className="zl-kpi-bar-val" style={{color:'var(--red)'}}>-{fmt$(Math.abs(s.al))}</span>
              </div>
            </div>
          </div>
        </div>

        {/* 4. Profit Factor */}
        <div className={`zl-kpi zl-kpi--${parseFloat(s.pf)>=1.5?'green':parseFloat(s.pf)>=1?'gold':'red'}`}>
          <div className="zl-kpi-label">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="4" y1="20" x2="4" y2="12"/><line x1="12" y1="20" x2="12" y2="6"/><line x1="20" y1="20" x2="20" y2="14"/></svg>
            Profit factor
          </div>
          <div className="zl-kpi-body">
            <div className={`zl-kpi-num zl-kpi-num--${parseFloat(s.pf)>=1.5?'green':parseFloat(s.pf)>=1?'gold':'red'}`}>{s.pf}</div>
            <svg width="52" height="52" viewBox="0 0 52 52" className="zl-kpi-ring">
              <circle cx="26" cy="26" r="20" fill="none" stroke="rgba(255,255,255,.07)" strokeWidth="5"/>
              <circle cx="26" cy="26" r="20" fill="none"
                stroke={parseFloat(s.pf)>=1.5?'var(--green)':parseFloat(s.pf)>=1?'var(--gold)':'var(--red)'}
                strokeWidth="5"
                strokeDasharray={`${2*Math.PI*20}`}
                strokeDashoffset={`${2*Math.PI*20*(1-Math.min(parseFloat(s.pf)||0,3)/3)}`}
                strokeLinecap="round"
                transform="rotate(-90 26 26)"/>
            </svg>
          </div>
        </div>

        {/* 5. Current Streak */}
        <div className={`zl-kpi zl-kpi--${cleanWeeks > 0 ? 'green' : 'red'}`}>
          <div className="zl-kpi-label">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
            Current streak
          </div>
          <div className="zl-kpi-body">
            <div className="zl-kpi-streak-wrap">
              <div className="zl-kpi-streak-row">
                <div className={`zl-kpi-streak-num${cleanWeeks <= 0 ? ' zl-kpi-streak-num--red' : ''}`}>
                  {Math.abs(cleanWeeks)}
                </div>
                <div>
                  <div style={{fontSize:'11px',fontWeight:600,color:'var(--white)',lineHeight:1}}>{Math.abs(cleanWeeks)} week{Math.abs(cleanWeeks)!==1?'s':''}</div>
                  <div style={{fontSize:'9px',color:'var(--gray)'}}>consistent</div>
                </div>
                <div className={`zl-kpi-streak-badge zl-kpi-streak-badge--${cleanWeeks>0?'green':'red'}`}>
                  {cleanWeeks > 0 ? '↑ Winning' : '↓ Losing'}
                </div>
              </div>
              <div className="zl-kpi-streak-row" style={{marginTop:4}}>
                <div className={`zl-kpi-streak-num${s.wr<50?' zl-kpi-streak-num--red':''}`}>{s.total}</div>
                <div>
                  <div style={{fontSize:'11px',fontWeight:600,color:'var(--white)',lineHeight:1}}>{s.total} trades</div>
                  <div style={{fontSize:'9px',color:'var(--gray)'}}>logged total</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          DEV STRIP (if unlocked)
      ═══════════════════════════════════════════════════════════ */}
      {trades.length >= MIN_TRADES_FOR_SCORE && (
        <div className="zl-dev-strip">
          {[
            { icon: '⚡', label: 'Discipline',  val: disciplineScore,   unit: '/100', color: 'gold',   max: 100 },
            { icon: '🎯', label: 'Consistency', val: consistencyScore,  unit: '/100', color: 'green',  max: 100 },
            { icon: '🔥', label: 'Win Streak',  val: cleanWeeks,        unit: ' wks', color: 'blue',   max: 10  },
            { icon: '🏆', label: 'Prop Ready',  val: propFirmReadiness, unit: '/100', color: 'purple', max: 100 },
          ].map(({ icon, label, val, unit, color, max }) => (
            <div key={label} className={`zl-dev-cell zl-dev-cell--${color}`}>
              <div className="zl-dev-icon">{icon}</div>
              <div className="zl-dev-content">
                <div className="zl-dev-label">{label}</div>
                <div className="zl-dev-val-row">
                  <span className={`zl-dev-val zl-dev-val--${color}`}>{val ?? '—'}</span>
                  <span className="zl-dev-unit">{unit}</span>
                </div>
                {val != null && (
                  <div className="zl-dev-bar">
                    <div className={`zl-dev-bar-fill zl-dev-bar-fill--${color}`} style={{width:`${Math.min(100,(val/max)*100)}%`}}/>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          BODY — Calendar + Right Sidebar
      ═══════════════════════════════════════════════════════════ */}
      <div className="zl-body">

        {/* ── CALENDAR COLUMN ── */}
        <div className="zl-cal-col">

          {/* AI Banner */}
          {aiInsights && (
            <div className="zl-ai-banner">
              <div className="zl-ai-banner-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
              </div>
              <div className="zl-ai-banner-content">
                <div className="zl-ai-banner-block">
                  <span className="zl-ai-banner-lbl">Trading Leak</span>
                  <span className="zl-ai-banner-txt">{aiInsights.weakness.split('.')[0]}.</span>
                </div>
                <div className="zl-ai-banner-block">
                  <span className="zl-ai-banner-lbl">Today's Focus</span>
                  <span className="zl-ai-banner-txt">{aiInsights.action.split('.')[0]}.</span>
                </div>
                {aiInsights.uplift > 0 && (
                  <div className="zl-ai-banner-block">
                    <span className="zl-ai-banner-lbl">Est. Recovery</span>
                    <span className="zl-ai-banner-txt">{fmt$(aiInsights.uplift)}/mo</span>
                  </div>
                )}
              </div>
              <button className="zl-ai-banner-cta" onClick={() => setPage('ai')}>Full Report →</button>
            </div>
          )}

          {/* Calendar card */}
          <div className="zl-cal-card">
            <div className="zl-cal-hdr">
              {/* Navigation */}
              <div className="zl-cal-nav">
                <button className="zl-cal-nav-btn" onClick={() => {
                  if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); }
                  else setCalMonth(m => m - 1);
                }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
                <span className="zl-cal-month-label">{MONTHS[calMonth]} {calYear}</span>
                <button className="zl-cal-nav-btn" onClick={() => {
                  if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); }
                  else setCalMonth(m => m + 1);
                }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
                <button className="zl-cal-this-month" onClick={() => { setCalMonth(today.getMonth()); setCalYear(today.getFullYear()); }}>
                  This month
                </button>
              </div>

              {/* Monthly stats */}
              <div className="zl-cal-hdr-stats">
                <span>Monthly stats:</span>
                <span><span className={`zl-cal-hdr-stat-val ${(calValueMode==='rr'?monthRR:monthPnl) >= 0 ? 'zl-cal-hdr-stat-val--green' : 'zl-cal-hdr-stat-val--red'}`}>{fmtCalVal(calValueMode==='rr'?monthRR:monthPnl)}</span></span>
                <span><span className="zl-cal-hdr-stat-val">{monthDays}</span> days</span>
                <span><span className={`zl-cal-hdr-stat-val ${monthWR >= 50 ? 'zl-cal-hdr-stat-val--green' : 'zl-cal-hdr-stat-val--red'}`}>{monthWR}%</span> WR</span>
                {(calValueMode==='rr' ? bestDayRR : bestDay) && <span>Best <span className="zl-cal-hdr-stat-val zl-cal-hdr-stat-val--green">{fmtCalVal(calValueMode==='rr'?bestDayRR.rr:bestDay.pnl)}</span></span>}
              </div>

              {/* Controls */}
              <div className="zl-cal-hdr-controls">
                <div ref={calSettingsRef} className="zl-cal-settings-wrap">
                  <button className={`zl-cal-ctrl-btn${calSettingsOpen?' active':''}`} onClick={() => setCalSettingsOpen(v => !v)} title="Display options">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  </button>
                  {calSettingsOpen && (
                    <div className="zl-cal-settings-dropdown">
                      <div className="zl-cal-settings-title">Display options</div>
                      <div className="zl-cal-settings-seg-label">Show values as</div>
                      <div className="zl-cal-seg">
                        <button className={`zl-cal-seg-btn${calValueMode==='money'?' active':''}`} onClick={() => setCalValueMode('money')}>Money</button>
                        <button className={`zl-cal-seg-btn${calValueMode==='rr'?' active':''}`} onClick={() => setCalValueMode('rr')}>R:R</button>
                      </div>
                      {[[hideTradeCount, setHideTradeCount, 'Hide trade count'],[hideWinPct, setHideWinPct, 'Hide win % per day']].map(([val, setter, label]) => (
                        <label key={label} className="zl-cal-settings-row">
                          <button onClick={() => setter(v => !v)} className={`zl-toggle${val?' on':''}`}><div className="zl-toggle-knob"/></button>
                          <span className="zl-cal-settings-label">{label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <button className="zl-cal-ctrl-btn" title="Save as image" onClick={handleCalScreenshot} disabled={screenshotting}>
                  {screenshotting
                    ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="icon-spin"><circle cx="12" cy="12" r="10" strokeOpacity=".2"/><path d="M12 2a10 10 0 0 1 10 10" stroke="var(--gold)"/></svg>
                    : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                  }
                </button>
              </div>
            </div>

            {/* Calendar grid + week sidebar */}
            <div className="zl-cal-grid-wrap">
              <div className="zl-cal-grid">
                {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
                  <div key={d} className="zl-cal-dow">{d}</div>
                ))}
                {Array.from({length: firstDay}, (_, i) => (
                  <div key={`e${i}`} className="zl-cal-day zl-cal-day--empty"/>
                ))}
                {Array.from({length: daysInMonth}, (_, i) => {
                  const day = i + 1;
                  const d = dayMap[day];
                  const isToday = today.getFullYear()===calYear && today.getMonth()===calMonth && today.getDate()===day;
                  const wr  = d ? Math.round(d.wins / d.count * 100) : 0;
                  const dispVal = d ? (calValueMode==='rr' ? d.rr : d.pnl) : 0;
                  const state = d ? (dispVal > 0 ? 'pos' : dispVal < 0 ? 'neg' : 'flat') : 'idle';
                  return (
                    <div
                      key={day}
                      className={`zl-cal-day zl-cal-day--${state}${isToday?' zl-cal-day--today':''}${d?' zl-cal-day--active':''}`}
                      onClick={d ? () => setCalDayModal({day, month:calMonth, year:calYear, data:d}) : undefined}
                    >
                      <span className="zl-cal-day-num">{day}</span>
                      {d && (
                        <>
                          <span className={`zl-cal-day-pnl ${dispVal>0?'pos':dispVal<0?'neg':'flat'}`}>
                            {fmtCalVal(dispVal)}
                          </span>
                          {(!hideTradeCount || !hideWinPct) && (
                            <span className="zl-cal-day-meta">
                              {!hideTradeCount && `${d.count} trade${d.count>1?'s':''}`}
                              {!hideTradeCount && !hideWinPct && ' · '}
                              {!hideWinPct && `${wr}%`}
                            </span>
                          )}
                          {d.sessions?.length > 0 && (
                            <span className="zl-cal-day-ses" title={d.sessions.join(' · ')}>
                              {d.sessions.slice(0,2).join(' · ')}
                            </span>
                          )}
                        </>
                      )}
                      {d && (
                        <div className="zl-cal-tooltip">
                          <div className="zl-cal-tt-row zl-cal-tt-header">
                            <span>{MONTHS[calMonth].slice(0,3)} {day}</span>
                            <span className={dispVal>=0?'c-green':'c-red'}>{fmtCalVal(dispVal)}</span>
                          </div>
                          <div className="zl-cal-tt-row"><span className="zl-cal-tt-lbl">Trades</span><span>{d.count}</span></div>
                          <div className="zl-cal-tt-row"><span className="zl-cal-tt-lbl">Win Rate</span><span className={wr>=50?'c-green':'c-red'}>{wr}%</span></div>
                          {d.sessions?.length > 0 && <div className="zl-cal-tt-row"><span className="zl-cal-tt-lbl">Sessions</span><span>{d.sessions.join(', ')}</span></div>}
                          <div className="zl-cal-tt-cta">Click to open →</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Week sidebar */}
              <div className="zl-cal-weeks">
                <div className="zl-cal-week-hdr">&nbsp;</div>
                {weeks.map((wk, i) => (
                  <div key={i} className="zl-cal-week-cell">
                    <span className="zl-cal-week-n">Week {wk.n}</span>
                    {wk.tradeDays > 0
                      ? <span className={`zl-cal-week-pnl ${(calValueMode==='rr'?wk.rr:wk.pnl)>=0?'c-green':'c-red'}`}>{fmtCalVal(calValueMode==='rr'?wk.rr:wk.pnl)}</span>
                      : <span className="zl-cal-week-off">OFF</span>
                    }
                    {wk.tradeDays > 0 && <span className="zl-cal-week-days">{wk.tradeDays} day{wk.tradeDays>1?'s':''}</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Below calendar: Session + Pair */}
          <div className="zl-below-cal">
            {sessionList.length > 0 && (
              <div className="zl-perf-panel">
                <div className="zl-perf-title">Session Performance</div>
                {sessionList.slice(0,5).map(([ses, d]) => {
                  const maxPnl = Math.max(...sessionList.map(([,x]) => Math.abs(x.pnl)), 1);
                  const wr = Math.round(d.wins / d.n * 100);
                  return (
                    <div key={ses} className="zl-session-row">
                      <span className="zl-session-name">{ses}</span>
                      <div className="zl-session-bar-track">
                        <div className={`zl-session-bar-fill ${d.pnl>=0?'pos':'neg'}`} style={{width:`${Math.min(100,Math.abs(d.pnl)/maxPnl*100)}%`}}/>
                      </div>
                      <span className={`zl-session-pnl ${d.pnl>=0?'c-green':'c-red'}`}>{d.pnl>=0?'':''}{fmt$(d.pnl)}</span>
                      <span className="zl-session-wr">{wr}%</span>
                    </div>
                  );
                })}
              </div>
            )}
            {(() => {
              const pairMap = {};
              trades.forEach(t => {
                const k = t.pair || 'Unknown';
                if (!pairMap[k]) pairMap[k] = { pnl:0, n:0, wins:0, rrs:[] };
                pairMap[k].pnl += t.result; pairMap[k].n++;
                if (t.result > 0) pairMap[k].wins++;
                const rr = parseFloat(t.rr||0); if (rr>0) pairMap[k].rrs.push(rr);
              });
              const pairs = Object.entries(pairMap).sort((a,b)=>Math.abs(b[1].pnl)-Math.abs(a[1].pnl)).slice(0,6);
              if (pairs.length < 2) return null;
              return (
                <div className="zl-perf-panel">
                  <div className="zl-perf-title">Pair Performance</div>
                  <div className="zl-pair-grid">
                    {pairs.map(([pair, d]) => {
                      const wr = Math.round(d.wins/d.n*100);
                      const avgRR = d.rrs.length ? (d.rrs.reduce((a,v)=>a+v,0)/d.rrs.length).toFixed(1) : '—';
                      return (
                        <div key={pair} className={`zl-pair-card ${d.pnl>=0?'zl-pair-card--pos':'zl-pair-card--neg'}`}>
                          <div className="zl-pair-name">{pair}</div>
                          <div className={`zl-pair-pnl ${d.pnl>=0?'c-green':'c-red'}`}>{d.pnl>=0?'':''}{fmt$(d.pnl)}</div>
                          <div className="zl-pair-meta">{d.n}t · {wr}% · {avgRR}R</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* ── RIGHT SIDEBAR ── */}
        <div className="zl-right-col">

          {/* KFX Score card */}
          <div className="zl-score-card">
            <div className="zl-score-title">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
              KFX Score
            </div>
            {score === null ? (
              <div className="zl-score-locked">
                <div className="zl-score-locked-bar">
                  <div className="zl-score-locked-bar-fill" style={{width:`${(trades.length/MIN_TRADES_FOR_SCORE)*100}%`}}/>
                </div>
                <div className="zl-score-locked-txt">{trades.length}/{MIN_TRADES_FOR_SCORE} trades to unlock score</div>
                <div className="zl-score-locked-grid">
                  {[['Win Rate',s.wr+'%',s.wr>=50?'c-green':'c-red'],['Avg R:R',s.avgrr+'R','c-gold'],['Net P&L',(s.net>=0?'':'')+fmt$(s.net),s.net>=0?'c-green':'c-red'],['Trades',s.total,'c-white']].map(([l,v,c]) => (
                    <div key={l} className="zl-score-locked-cell">
                      <div className="zl-score-locked-cell-lbl">{l}</div>
                      <div className={`zl-score-locked-cell-val ${c}`}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <div className="zl-score-body">
                  <div className="zl-score-radar">
                    <canvas ref={radarRef} className="radar-cv"/>
                  </div>
                  <div className="zl-score-side">
                    <div className="zl-score-num">{score}</div>
                    <div className="zl-score-label" style={{color:sl?.color}}>{sl?.text}</div>
                    <div className="zl-score-sub">out of 100</div>
                  </div>
                </div>
                <div className="zl-score-bar-wrap">
                  <div className="zl-score-bar">
                    <div className="zl-score-bar-fill" style={{width:`${score}%`}}/>
                  </div>
                  <div className="zl-score-ticks">
                    {['0','25','50','75','100'].map(t => <span key={t}>{t}</span>)}
                  </div>
                </div>
                <div className="zl-score-stats">
                  {[
                    ['Win Rate', s.wr+'%', s.wr>=50?'c-green':'c-red'],
                    ['Avg R:R', s.avgrr+'R', 'c-gold'],
                    ['P.Factor', s.pf, parseFloat(s.pf)>=1.5?'c-green':'c-gold'],
                  ].map(([l,v,c]) => (
                    <div key={l} className="zl-score-stat">
                      <div className="zl-score-stat-lbl">{l}</div>
                      <div className={`zl-score-stat-val ${c}`}>{v}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Equity curve */}
          <div className="zl-score-card">
            <div className="zl-score-title" style={{marginBottom:8,justifyContent:'space-between'}}>
              <span style={{display:'flex',alignItems:'center',gap:6}}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/></svg>
                Equity Curve
              </span>
              <div style={{display:'flex',gap:3}}>
                {['ALL','3M','1M'].map(f => (
                  <button key={f} className={`perf-btn${perfFilter===f?' active':''}`} onClick={() => setPerfFilter(f)}>{f}</button>
                ))}
              </div>
            </div>
            <PerfOverviewChart trades={trades} filter={perfFilter}/>
          </div>

          {/* Recent trades */}
          <div className="zl-recent-card">
            <div className="zl-recent-hdr">
              <div className="zl-recent-title">Recent Trades</div>
              <div className="zl-recent-limit-btns">
                {[3,5].map(n => (
                  <button key={n} onClick={() => setRecentLimit(n)} className={`zl-recent-limit-btn${recentLimit===n?' active':''}`}>{n}</button>
                ))}
              </div>
            </div>
            {trades.length >= 5 && (
              <div className="zl-recent-stats-strip">
                {[['Last 10',recentWR+'%',recentWR>=s.wr?'c-green':'c-red'],['P&L',(recentPnl>=0?'':'')+fmt$(recentPnl),recentPnl>=0?'c-green':'c-red'],['Overall',s.wr+'%','c-dim']].map(([l,v,c]) => (
                  <div key={l}>
                    <div className="zl-recent-stats-lbl">{l}</div>
                    <div className={`zl-recent-stats-val ${c}`}>{v}</div>
                  </div>
                ))}
              </div>
            )}
            <div>
              {recent.length === 0
                ? <div style={{fontSize:11,color:'var(--gray)',padding:'12px 0',textAlign:'center'}}>No trades yet.</div>
                : recent.map(t => (
                  <div key={t.id} className="zl-recent-row" onClick={() => setViewTrade(t)}>
                    <div className={`zl-recent-bar ${t.result>0?'pos':'neg'}`}/>
                    <div className="zl-recent-main">
                      <div className="zl-recent-top">
                        <span className="zl-recent-pair">{t.pair}</span>
                        <span className={`zl-recent-dir ${t.dir==='BUY'?'buy':'sell'}`}>{t.dir}</span>
                      </div>
                      <div className="zl-recent-date">{t.date}{t.session?' · '+t.session:''}</div>
                    </div>
                    <div className="zl-recent-right">
                      <div className={`zl-recent-pnl ${t.result>0?'pos':'neg'}`}>{fmt$(t.result)}</div>
                      <div className="zl-recent-rr">{t.rr}R</div>
                    </div>
                  </div>
                ))
              }
            </div>
            <div className="zl-recent-actions">
              <button onClick={() => setPage('j')} className="zl-all-trades-btn">
                <div className="zl-all-trades-lbl">All Trades →</div>
                <div className="zl-all-trades-count">{trades.length} total</div>
              </button>
              <button className="zl-log-btn" onClick={() => onNewTrade({ activeAccount })}>+ Log</button>
            </div>
          </div>

          {/* Action buttons */}
          <div className="zl-actions">
            <button onClick={() => setPage('analytics')} className="zl-action-btn zl-action-btn--ghost">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="4" y1="20" x2="4" y2="12"/><line x1="12" y1="20" x2="12" y2="6"/><line x1="20" y1="20" x2="20" y2="14"/></svg>
              Analytics
            </button>
            <button onClick={() => setShowImport(true)} className="zl-action-btn zl-action-btn--ghost">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
              Import
            </button>
          </div>
        </div>
      </div>

      {/* ── CALENDAR DAY MODAL (Zella-style summary) ── */}
      {calDayModal && (() => {
        const dayTrades  = calDayModal.data.trades;
        const ds         = calcStats(dayTrades);
        const dayIdx     = allDaysData.findIndex(d => d.year===calDayModal.year && d.month===calDayModal.month && d.day===calDayModal.day);
        const hasPrev    = dayIdx > 0;
        const hasNext    = dayIdx !== -1 && dayIdx < allDaysData.length - 1;
        const gotoDay = (idx) => {
          const d = allDaysData[idx];
          if (!d) return;
          setCalMonth(d.month); setCalYear(d.year);
          setCalDayModal({ day: d.day, month: d.month, year: d.year, data: { pnl: d.pnl, count: d.count, trades: d.trades } });
        };
        const sortedTrades = [...dayTrades].sort((a,b) => (a.time||'').localeCompare(b.time||''));
        let run = 0;
        const eqPts  = sortedTrades.map(t => { run += (t.result||0); return run; });
        const volume = dayTrades.reduce((a,t) => a + (parseFloat(t.lotSize)||0), 0);
        const netColor = ds.net >= 0 ? 'var(--green)' : 'var(--red)';

        return (
          <div className="modal-bg open" onClick={() => setCalDayModal(null)}>
            {hasPrev && (
              <button className="kdd-nav-arrow kdd-nav-l" onClick={e => { e.stopPropagation(); gotoDay(dayIdx-1); }} aria-label="Previous day">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
            )}
            {hasNext && (
              <button className="kdd-nav-arrow kdd-nav-r" onClick={e => { e.stopPropagation(); gotoDay(dayIdx+1); }} aria-label="Next day">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            )}

            <div className="modal modal-w900 kdd-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-bar kdd-bar">
                <div>
                  <div className="modal-ttl kdd-ttl">{MONTHS[calDayModal.month]} {calDayModal.day}, {calDayModal.year}</div>
                  <div className="modal-bar-sub kdd-sub">
                    Net P&amp;L&nbsp;
                    <span className="kdd-net" style={{color:netColor}}>
                      {ds.net>=0?'+':'-'}${Math.abs(ds.net).toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="modal-bar-actions kdd-actions">
                  <button className="btn-ghost-gold" onClick={() => { setCalDayModal(null); setPage('nb', { prefillTrades: dayTrades, prefillDate: `${MONTHS[calDayModal.month]} ${calDayModal.day}, ${calDayModal.year}` }); }} title="Take Notes">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    <span>Take Notes</span>
                  </button>
                  <button className="modal-x" onClick={() => setCalDayModal(null)}>×</button>
                </div>
              </div>

              <div className="modal-scroll-body kdd-body">
                {/* chart + kpi grid */}
                <div className="kdd-top-row">
                  <div className="kdd-chart-wrap">
                    {eqPts.length >= 2 ? (
                      <svg viewBox="0 0 600 160" preserveAspectRatio="none" className="kdd-chart-svg">
                        <defs>
                          <linearGradient id="kddEq" x1="0" x2="0" y1="0" y2="1">
                            <stop offset="0%" stopColor={netColor} stopOpacity=".28"/>
                            <stop offset="100%" stopColor={netColor} stopOpacity="0"/>
                          </linearGradient>
                        </defs>
                        {(() => {
                          const mn = Math.min(0, ...eqPts), mx = Math.max(0, ...eqPts), range = (mx-mn)||1;
                          const W=600, H=160, PAD=10;
                          const pathD = eqPts.map((v,i) => `${i===0?'M':'L'}${(i/(eqPts.length-1))*W},${H-PAD-((v-mn)/range)*(H-PAD*2)}`).join(' ');
                          const zeroY = H-PAD-((0-mn)/range)*(H-PAD*2);
                          return (<>
                            <line x1="0" y1={zeroY} x2={W} y2={zeroY} stroke="rgba(255,255,255,.08)" strokeDasharray="3 4"/>
                            <path d={`${pathD} L${W},${H} L0,${H} Z`} fill="url(#kddEq)"/>
                            <path d={pathD} fill="none" stroke={netColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </>);
                        })()}
                      </svg>
                    ) : (
                      <div className="kdd-chart-empty">Not enough data for an intraday curve</div>
                    )}
                  </div>
                  <div className="kdd-kpi-grid">
                    {[
                      ['Total Trades', ds.total, null],
                      ['Winners / Losers', `${ds.wins} / ${ds.losses}`, null],
                      ['Win Rate', `${ds.wr}%`, ds.wr>=50?'c-green':'c-red'],
                      ['Volume (lots)', volume.toFixed(2), null],
                      ['Profit Factor', ds.pf, (parseFloat(ds.pf)||0)>=1?'c-green':'c-red'],
                      ['Avg R:R', `${ds.avgrr}R`, null],
                    ].map(([l,v,c]) => (
                      <div key={l} className="kdd-kpi-cell">
                        <div className="kdd-kpi-lbl">{l}</div>
                        <div className={`kdd-kpi-val ${c||''}`}>{v}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* trade table */}
                <div className="kdd-table-wrap">
                  <table className="kdd-table">
                    <thead>
                      <tr>
                        <th>Time</th><th>Pair</th><th>Side</th><th>Entry</th><th>R:R</th><th>Strategy</th><th style={{textAlign:'right'}}>P&amp;L</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedTrades.map(t => {
                        const win = t.result > 0;
                        const amtDisplay = t.result ? `${win?'+$':'-$'}${Math.abs(t.result).toFixed(2)}` : '--';
                        return (
                          <tr key={t.id} className="kdd-row" onClick={() => setViewTrade(t)}>
                            <td className="c-gold-80">{t.time || '—'}</td>
                            <td className="kdd-pair">{t.pair}</td>
                            <td><span className={`kday-dir-chip ${t.dir==='BUY'?'buy':'sell'}`}>{t.dir}</span></td>
                            <td>{t.entry || '—'}</td>
                            <td>{t.rr ? `${t.rr}R` : '—'}</td>
                            <td>{t.strat || '—'}</td>
                            <td style={{textAlign:'right'}} className={win?'c-green':'c-red'}>{amtDisplay}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── VIEW TRADE MODAL ── */}
      {viewTrade && (() => {
        const t = viewTrade, win = t.result > 0;
        const amtDisplay = t.result ? `${win?'+$':'-$'}${Math.abs(t.result).toFixed(2)}` : '--';
        return (
          <div className="modal-bg open" onClick={() => setViewTrade(null)}>
            <div className="modal modal-w820" onClick={e => e.stopPropagation()}>
              <div className="modal-bar">
                <div>
                  <div className="modal-ttl">{t.pair} · {t.dir}</div>
                  <div className="modal-bar-sub">{t.date}{t.time?` · ${t.time}`:''}{t.session?` · ${t.session}`:''}</div>
                </div>
                <div className="modal-bar-actions-10">
                  <span className={`view-trade-amt ${win?'pos':'neg'}`}>{amtDisplay}</span>
                  <button className="modal-x" onClick={() => setViewTrade(null)}>×</button>
                </div>
              </div>
              <div className="vt-body">
                <div className="vt-grid4">
                  {[['Pair',t.pair,null],['Direction',t.dir,t.dir==='BUY'?'c-green':'c-red'],['Entry',t.entry,null],['Time',t.time||'—','c-gold-80']].map(([l,v,c]) => (
                    <div key={l} className="vcell"><div className="vcell-l">{l}</div><div className={`vcell-v ${c||''}`}>{v||'--'}</div></div>
                  ))}
                </div>
                <div className="vt-grid4">
                  {[['Stop Loss',t.sl,'c-red'],['Take Profit',t.tp,'c-green'],['P&L ($)',amtDisplay,win?'c-green':'c-red'],['R:R',t.rr?`${t.rr}R`:'--','c-gold']].map(([l,v,c]) => (
                    <div key={l} className="vcell"><div className="vcell-l">{l}</div><div className={`vcell-v ${c} ${l==='P&L ($)'?'vcell-v-pnl':''}`}>{v}</div></div>
                  ))}
                </div>
                <div className="vt-grid4-mb16">
                  <div className="vcell"><div className="vcell-l">Pips</div><div className={`vcell-v ${t.pips>0?'c-green':'c-red'}`}>{t.pips>0?'+':''}{t.pips}</div></div>
                  <div className="vcell"><div className="vcell-l">Lot Size</div><div className="vcell-v">{t.lotSize||'--'}</div></div>
                  <div className="vcell"><div className="vcell-l">Strategy</div><div className="vcell-v">{t.strat||'—'}</div></div>
                  <div className="vcell"><div className="vcell-l">Emotion</div><div className="vcell-v">{t.emotion||'—'}</div></div>
                </div>
                {t.notes  && <div className="vnotes vnotes-mb6"><div className="vnotes-l">Pre-Trade Notes</div><div className="vnotes-t">{t.notes}</div></div>}
                {t.review && <div className="vnotes vnotes-mb16"><div className="vnotes-l">Post-Trade Review</div><div className="vnotes-t">{t.review}</div></div>}
                {t.img    && <div className="vt-img-wrap"><img src={t.img} alt="chart" className="vt-img"/></div>}
                <div className="vt-footer">
                  <button className="btn-outline" onClick={() => setViewTrade(null)}>Close</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {confirm && <ConfirmModal message={confirm.message} onConfirm={() => { confirm.onConfirm(); setConfirm(null); }} onCancel={() => setConfirm(null)}/>}

      {showImport && (
        <ImportModal onImport={onImportTrades} onClose={() => setShowImport(false)} nextId={nextId} activeAccountId={activeAccountId}/>
      )}
    </div>
  );
}

const PDF_CSS = `
  .section-hd-label { font-size: 9px; font-weight: 700; letter-spacing: .18em; text-transform: uppercase; color: #9a8060; white-space: nowrap; }
  .section-hd-icon { font-size: 13px; }

  /* ── KPI GRID ── */
  .kpi-grid   { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 4px; }
  .kpi-grid-2 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }

  /* ── TABLES ── */
  table { width: 100%; border-collapse: collapse; }
  thead tr { background: #f5f0e6; }
  th { padding: 10px 14px; text-align: left; font-size: 9px; font-weight: 700; letter-spacing: .14em; text-transform: uppercase; color: #8a7a58; border-bottom: 2px solid #e8e0cc; }
  td { padding: 10px 14px; border-bottom: 1px solid #f0ead6; font-size: 12px; vertical-align: middle; }
  tbody tr:last-child td { border-bottom: none; }
  tbody tr:hover { background: #faf7ef !important; }

  /* ── FOOTER ── */
  .report-footer { background: #f5f2eb; padding: 20px 56px; display: flex; align-items: center; justify-content: space-between; border-top: 1px solid #e8e0cc; }
  .report-footer-l { font-size: 9px; font-weight: 700; letter-spacing: .18em; text-transform: uppercase; color: #b0a080; }
  .report-footer-r { font-size: 9px; color: #c0b090; }

  /* ── SUMMARY STRIP ── */
  .summary-strip { display: flex; gap: 0; border: 1px solid #e8e0cc; border-radius: 8px; overflow: hidden; margin-bottom: 4px; }
  .summary-item { flex: 1; padding: 14px 18px; border-right: 1px solid #e8e0cc; background: #fdfcf9; }
  .summary-item:last-child { border-right: none; }
  .summary-item-label { font-size: 8px; font-weight: 600; letter-spacing: .14em; text-transform: uppercase; color: #aaa; margin-bottom: 4px; }
  .summary-item-val { font-size: 18px; font-weight: 800; letter-spacing: -.02em; line-height: 1; }

  @media print {
    html, body { background: #fff !important; }
    .page { box-shadow: none; max-width: 100%; }
    .cover { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  }

  /* ── COVER TRADER INFO ── */
  .cover-trader-info { margin-top: 14px; display: flex; flex-direction: column; gap: 5px; }
  .cover-trader-name { font-size: 12px; color: rgba(240,234,214,.55); }
  .cover-trader-name-val { color: rgba(240,234,214,.85); font-weight: 500; }

  /* ── MODIFIER UTILITIES ── */
  .section-hd--first { margin-top: 0; }
  .summary-strip--mb12 { margin-bottom: 12px; }
  .kpi-grid--mb32 { margin-bottom: 32px; }
`;

/* ── PDF_STYLES ── standalone stylesheet for the export popup (no app CSS loads there) ── */
const PDF_STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { background: #f5f3ec; }
  body { font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; color: #1a1a1a; -webkit-font-smoothing: antialiased; }

  .page { width: 100%; }

  /* ── Cover ── */
  .cover {
    position: relative;
    height: 297mm;
    background: linear-gradient(160deg, #0d0d0d 0%, #1a1710 55%, #0d0d0d 100%);
    color: #f0ead6;
    padding: 64px 56px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    overflow: hidden;
    page-break-after: always;
  }
  .cover-noise {
    position: absolute; inset: 0;
    background-image: radial-gradient(circle at 1px 1px, rgba(255,255,255,.035) 1px, transparent 0);
    background-size: 3px 3px;
    pointer-events: none;
  }
  .cover-glow {
    position: absolute; top: -20%; right: -10%;
    width: 60%; height: 60%;
    background: radial-gradient(circle, rgba(176,141,46,.25) 0%, transparent 70%);
    pointer-events: none;
  }
  .cover-line { position: absolute; top: 64px; left: 56px; right: 56px; height: 1px; background: rgba(240,234,214,.15); }
  .cover-brand {
    position: relative; z-index: 1;
    font-size: 11px; font-weight: 600; letter-spacing: .18em; text-transform: uppercase;
    color: #d4a843; margin-bottom: 28px;
  }
  .cover-title {
    position: relative; z-index: 1;
    font-size: 52px; font-weight: 800; letter-spacing: -.02em; line-height: 1.05;
    margin-bottom: 18px;
  }
  .cover-title span { color: #d4a843; }
  .cover-sub {
    position: relative; z-index: 1;
    font-size: 13px; color: rgba(240,234,214,.55); margin-bottom: 40px;
  }
  .cover-meta {
    position: relative; z-index: 1;
    display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 28px;
  }
  .cover-pill {
    display: flex; align-items: center; gap: 7px;
    font-size: 11px; font-weight: 500; color: rgba(240,234,214,.85);
    background: rgba(255,255,255,.06); border: 1px solid rgba(255,255,255,.1);
    border-radius: 20px; padding: 6px 14px;
  }
  .cover-pill .dot { width: 5px; height: 5px; border-radius: 50%; background: #d4a843; display: inline-block; }
  .cover-trader-info {
    position: relative; z-index: 1;
    border-top: 1px solid rgba(255,255,255,.12);
    padding-top: 20px; margin-bottom: 28px;
  }
  .cover-trader-name { font-size: 12px; color: rgba(240,234,214,.55); margin-bottom: 6px; }
  .cover-trader-name-val { color: #f0ead6; font-weight: 600; }
  .cover-bar-wrap { position: relative; z-index: 1; }
  .cover-bar-label {
    display: flex; justify-content: space-between; align-items: baseline;
    font-size: 11px; font-weight: 600; color: #f0ead6; margin-bottom: 8px;
  }
  .cover-bar { width: 100%; height: 6px; background: rgba(255,255,255,.1); border-radius: 3px; overflow: hidden; }
  .cover-bar-fill { height: 100%; background: linear-gradient(90deg, #b08d2e, #d4a843); border-radius: 3px; }

  /* ── Body ── */
  .body { padding: 48px 56px 32px; }

  .section-hd {
    display: flex; align-items: center; gap: 10px;
    margin: 36px 0 16px;
  }
  .section-hd--first { margin-top: 0; }
  .section-hd-icon { font-size: 14px; }
  .section-hd-label {
    font-size: 12px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase;
    color: #1a1a1a; white-space: nowrap;
  }
  .section-hd-line { flex: 1; height: 1px; background: #ede8da; }

  .summary-strip {
    display: flex;
    border: 1px solid #ede8da; border-radius: 8px;
    overflow: hidden;
  }
  .summary-strip--mb12 { margin-bottom: 12px; }
  .summary-item {
    flex: 1; padding: 16px 18px;
    border-right: 1px solid #ede8da;
  }
  .summary-item:last-child { border-right: none; }
  .summary-item-label {
    font-size: 9px; font-weight: 600; letter-spacing: .12em; text-transform: uppercase;
    color: #9a8c6a; margin-bottom: 8px;
  }
  .summary-item-val { font-size: 19px; font-weight: 800; letter-spacing: -.01em; }

  .kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
  .kpi-grid--mb32 { margin-bottom: 32px; }

  table { width: 100%; border-collapse: collapse; font-size: 11.5px; }
  thead th {
    text-align: left; font-size: 9px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase;
    color: #9a8c6a; padding: 0 10px 8px; border-bottom: 1.5px solid #1a1a1a;
  }
  tbody td { padding: 9px 10px; border-bottom: 1px solid #f1ede0; }
  tbody tr:last-child td { border-bottom: none; }

  .report-footer {
    display: flex; justify-content: space-between;
    padding: 18px 56px; border-top: 1px solid #ede8da;
    font-size: 9.5px; color: #a09a86;
  }

  @media print {
    @page { size: A4; margin: 0; }
    .cover { page-break-after: always; }
    table { page-break-inside: avoid; }
    .kpi-grid, .summary-strip { page-break-inside: avoid; }
  }
`;

/* ── exportDashboardPDF ── */
function exportDashboardPDF(s, analytics, trades, radarRef, userInfo, activeAccount) {
  const { maxDD, maxDDPct, sessionList } = analytics;

  /* ── helpers ── */
  const money  = (n) => `$${Math.abs(Number(n)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const signed = (n) => `${Number(n) >= 0 ? '+' : '-'}${money(n)}`;
  const GREEN  = '#16a34a';
  const RED    = '#dc2626';
  const GOLD   = '#b08d2e';
  const GOLD_L = '#d4a843';

  /* ── session rows ── */
  const sessionRows = (sessionList || []).slice(0, 6).map(([ses, d], i) => {
    const wr   = Math.round(d.wins / d.n * 100);
    const pnlC = d.pnl >= 0 ? GREEN : RED;
    const bg   = i % 2 === 0 ? '#ffffff' : '#fafaf8';
    return `<tr style="background:${bg}">
      <td style="font-weight:600;color:#1a1a1a">${ses}</td>
      <td>
        <div style="display:flex;align-items:center;gap:8px">
          <div style="flex:1;height:4px;background:#e8e4d8;border-radius:2px;max-width:80px">
            <div style="width:${wr}%;height:4px;background:${wr>=60?GREEN:wr>=40?GOLD_L:RED};border-radius:2px"></div>
          </div>
          <span style="font-weight:700;color:${wr>=60?GREEN:wr>=40?GOLD:RED}">${wr}%</span>
        </div>
      </td>
      <td style="font-weight:700;color:${pnlC}">${signed(d.pnl)}</td>
      <td style="color:#666;text-align:center">${d.n}</td>
    </tr>`;
  }).join('');

  /* ── trade rows ── */
  const sortedTrades = [...trades].sort((a, b) => {
    const d = b.date.localeCompare(a.date); if (d !== 0) return d;
    return (Number(b.id) || 0) - (Number(a.id) || 0);
  }).slice(0, 10);

  const tradeRows = sortedTrades.map((t, i) => {
    const win = t.result >= 0;
    const bg  = i % 2 === 0 ? '#ffffff' : '#fafaf8';
    const rrN = parseFloat(t.rr) || 0;
    return `<tr style="background:${bg}">
      <td style="color:#666;font-size:11px">${t.date}</td>
      <td style="font-weight:700;color:#1a1a1a;letter-spacing:.04em">${t.pair}</td>
      <td><span style="display:inline-block;padding:2px 8px;border-radius:3px;font-size:10px;font-weight:700;background:${t.dir==='BUY'?'rgba(22,163,74,.1)':'rgba(220,38,38,.1)'};color:${t.dir==='BUY'?GREEN:RED}">${t.dir}</span></td>
      <td style="font-weight:700;color:${win?GREEN:RED}">${signed(t.result)}</td>
      <td style="font-weight:600;color:${rrN>=2?GREEN:rrN>=1?GOLD:RED}">${rrN > 0 ? rrN.toFixed(2)+'R' : '—'}</td>
      <td style="color:#888;font-size:11px">${t.strat || t.session || '—'}</td>
    </tr>`;
  }).join('');

  /* ── win/loss mini bar for cover ── */
  const wins   = trades.filter(t => t.result > 0).length;
  const losses = trades.filter(t => t.result < 0).length;
  const total  = trades.length || 1;
  const winPct = Math.round(wins / total * 100);

  /* ── KPI card helper ── */
  const kpi = (label, value, color = '#1a1a1a', sub = '') => `
    <div style="background:#fff;border:1px solid #ede8da;border-radius:8px;padding:18px 20px;position:relative;overflow:hidden">
      <div style="position:absolute;top:0;left:0;right:0;height:2px;background:${GOLD_L}"></div>
      <div style="font-size:9px;font-weight:600;letter-spacing:.14em;text-transform:uppercase;color:#9a8c6a;margin-bottom:8px">${label}</div>
      <div style="font-size:22px;font-weight:800;color:${color};letter-spacing:-.02em;line-height:1">${value}</div>
      ${sub ? `<div style="font-size:10px;color:#aaa;margin-top:5px">${sub}</div>` : ''}
    </div>`;

  const netColor  = s.net  >= 0 ? GREEN : RED;
  const pfNum     = parseFloat(s.pf) || 0;
  const pfColor   = pfNum >= 1.5 ? GREEN : pfNum >= 1 ? GOLD : RED;
  const rrNum     = parseFloat(s.avgrr) || 0;
  const rrColor   = rrNum >= 2 ? GREEN : rrNum >= 1 ? GOLD : RED;
  const expNum    = parseFloat(s.expectancy) || 0;
  const expColor  = expNum >= 0 ? GREEN : RED;
  const ddColor   = maxDDPct > 10 ? RED : maxDDPct > 5 ? GOLD : '#1a1a1a';

  const genDate   = new Date().toLocaleDateString('en-US', { year:'numeric', month:'long', day:'numeric' });
  const genTime   = new Date().toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit' });

  const traderName   = userInfo?.full_name || userInfo?.name || userInfo?.email || 'Trader';
  const traderEmail  = userInfo?.email || '';
  const accountLabel = activeAccount?.name
    ? `${activeAccount.name}${activeAccount.type ? ' · ' + activeAccount.type : ''}`
    : 'All Accounts';

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>KFX Performance Report</title>
<link href="https://fonts.googleapis.com/css2?family=Inter:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400&display=swap" rel="stylesheet">
<style>${PDF_STYLES}<\/style>
<\/head>
<body>
<div class="page">

  <!-- COVER -->
  <div class="cover">
    <div class="cover-noise"></div>
    <div class="cover-glow"></div>
    <div class="cover-line"></div>
    <div class="cover-brand">KFX University &nbsp;·&nbsp; Performance Report</div>
    <div class="cover-title">Trading<br><span>Performance</span><br>Summary</div>
    <div class="cover-sub">Confidential · For internal review only</div>
    <div class="cover-meta">
      <div class="cover-pill"><span class="dot"></span>${genDate}</div>
      <div class="cover-pill"><span class="dot"></span>${trades.length} Trades Analysed</div>
      <div class="cover-pill"><span class="dot"></span>Generated ${genTime}</div>
    </div>
    <div class="cover-trader-info">
      <div class="cover-trader-name">Name: <span class="cover-trader-name-val">${traderName}${traderEmail && traderEmail !== traderName ? ' &nbsp;·&nbsp; ' + traderEmail : ''}</span></div>
      <div class="cover-trader-name">Account: <span class="cover-trader-name-val">${accountLabel}</span></div>
    </div>
    <div class="cover-bar-wrap">
      <div class="cover-bar-label">
        <span>Win Rate</span>
        <span style="color:rgba(240,234,214,.55)">${winPct}% · ${wins}W / ${losses}L</span>
      </div>
      <div class="cover-bar"><div class="cover-bar-fill" style="width:${winPct}%"></div></div>
    </div>
  </div>

  <!-- BODY -->
  <div class="body">

    <!-- SUMMARY STRIP -->
    <div class="section-hd section-hd--first">
      <span class="section-hd-icon">📊</span>
      <span class="section-hd-label">Key Statistics</span>
      <div class="section-hd-line"></div>
    </div>

    <div class="summary-strip summary-strip--mb12">
      <div class="summary-item">
        <div class="summary-item-label">Net P&amp;L</div>
        <div class="summary-item-val" style="color:${netColor}">${signed(s.net)}</div>
      </div>
      <div class="summary-item">
        <div class="summary-item-label">Win Rate</div>
        <div class="summary-item-val" style="color:${s.wr>=60?GREEN:s.wr>=50?GOLD:RED}">${s.wr}%</div>
      </div>
      <div class="summary-item">
        <div class="summary-item-label">Avg R:R</div>
        <div class="summary-item-val" style="color:${rrColor}">${s.avgrr}R</div>
      </div>
      <div class="summary-item">
        <div class="summary-item-label">Profit Factor</div>
        <div class="summary-item-val" style="color:${pfColor}">${s.pf}</div>
      </div>
      <div class="summary-item">
        <div class="summary-item-label">Total Trades</div>
        <div class="summary-item-val" style="color:#1a1a1a">${trades.length}</div>
      </div>
    </div>

    <div class="kpi-grid kpi-grid--mb32">
      ${kpi('Max Drawdown', `-${money(maxDD)}`, ddColor, `${maxDDPct.toFixed(1)}% of account`)}
      ${kpi('Expectancy', signed(s.expectancy), expColor, 'per trade avg')}
      ${kpi('Avg Win', `+${money(s.aw)}`, GREEN, `${wins} winning trades`)}
      ${kpi('Avg Loss', `-${money(Math.abs(s.al))}`, RED, `${losses} losing trades`)}
    </div>

    ${sessionRows ? `
    <div class="section-hd">
      <span class="section-hd-icon">🕐</span>
      <span class="section-hd-label">Session Performance</span>
      <div class="section-hd-line"></div>
    </div>
    <table style="margin-bottom:32px">
      <thead><tr><th>Session</th><th>Win Rate</th><th>Net P&amp;L</th><th style="text-align:center">Trades</th></tr></thead>
      <tbody>${sessionRows}</tbody>
    </table>` : ''}

    <div class="section-hd">
      <span class="section-hd-icon">📋</span>
      <span class="section-hd-label">Last 10 Trades</span>
      <div class="section-hd-line"></div>
    </div>
    <table>
      <thead><tr><th>Date</th><th>Pair</th><th>Dir</th><th>P&amp;L</th><th>R:R</th><th>Strategy / Session</th></tr></thead>
      <tbody>${tradeRows}</tbody>
    </table>

  </div>

  <!-- FOOTER -->
  <div class="report-footer">
    <div class="report-footer-l">KFX University · Performance Report</div>
    <div class="report-footer-r">Generated ${genDate} at ${genTime} · Confidential</div>
  </div>

</div>
<script>window.onload = () => window.print();<\/script>
<\/body>
<\/html>`;

  const w = window.open('', '_blank');
  if (!w) return alert('Allow popups to export PDF.');
  w.document.write(html);
  w.document.close();
}